import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { getOverviewRevenueExpenseAPI } from "services/api/dashboard/api";
import { OverviewRevenueExpenseResponse } from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../common/DashboardTooltip";
import CardHeader, { DeltaBadge } from "../../common/CardHeader";
import { BarTooltipRow, BarTooltipTitle, LegendTooltip } from "../../common/HoverPopup";
import { compact, negative, positive, text } from "../../common/dashboardPalette";
import { useDashboardDateRange } from "../../../context/DashboardDateRangeContext";
import { rangeLabel } from "ui/components/DateRangePicker";
import AxisLabels from "../../common/AxisLabels";

const RevenueExpenseCard: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { range: dateRange } = useDashboardDateRange();
  const [data, setData] = useState<OverviewRevenueExpenseResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Klik na bucket -> pretraga troškova za taj raspon (datumi u query paramovima).
  const handleBucketClick = (i: number) => {
    const m = months[i];
    if (!m?.start || !m?.end) return;
    const from = new Date(m.start);
    const toInclusive = new Date(new Date(m.end).getTime() - 1); // ekskluzivni kraj -> zadnji dan
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const params = new URLSearchParams({
      expenseDateFrom: fmt(from),
      expenseDateTo: fmt(toInclusive),
    });
    navigate(`${FinanceRoutes.ExpenseSearchPageRouter}?${params.toString()}`);
  };

  useEffect(() => {
    setIsLoading(true);
    getOverviewRevenueExpenseAPI(dateRange.from, dateRange.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [dateRange.from, dateRange.to]);

  if (isLoading) return <div className="relative h-full"><Loader isLoading={true}> </Loader></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Nema podataka</div>;

  const months = data.months.map((m) => ({
    label: m.monthLabel || m.month,
    revenue: m.revenue,
    expense: m.expense,
    start: m.start,
    end: m.end,
  }));

  const revenue = months.map((m) => m.revenue);
  const expenses = months.map((m) => m.expense);

  const allValues = [...revenue, ...expenses];
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues) * 0.8;
  const range = maxVal - minVal || 1;

  const totalRevenue = data.totalRevenue;
  const totalExpenses = data.totalExpense;
  const profit = data.profit ?? totalRevenue - totalExpenses;
  const marginPercent = data.marginPercent ?? (totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0);

  const last = months.length - 1;
  const prev = last - 1;
  const revChange = prev >= 0 && revenue[prev] > 0 ? ((revenue[last] - revenue[prev]) / revenue[prev]) * 100 : 0;
  const expChange = prev >= 0 && expenses[prev] > 0 ? ((expenses[last] - expenses[prev]) / expenses[prev]) * 100 : 0;

  const chartW = 550;
  const chartH = 120;
  const padX = 10;
  const padY = 5;

  const toPoint = (val: number, i: number) => {
    const x = padX + (i / Math.max(months.length - 1, 1)) * (chartW - 2 * padX);
    const y = padY + (1 - (val - minVal) / range) * (chartH - 2 * padY);
    return { x, y };
  };

  const buildSmoothPath = (values: number[]) => {
    const points = values.map((v, i) => toPoint(v, i));
    if (points.length < 2) return "";
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const t = 0.2;
      const cp1x = p0.x + (p1.x - p0.x) * t;
      const cp2x = p1.x - (p1.x - p0.x) * t;
      d += ` C ${cp1x} ${p0.y}, ${cp2x} ${p1.y}, ${p1.x} ${p1.y}`;
    }
    return d;
  };

  const buildAreaPath = (values: number[]) => {
    if (values.length < 2) return "";
    const line = buildSmoothPath(values);
    const lastPoint = toPoint(values[values.length - 1], values.length - 1);
    const firstPoint = toPoint(values[0], 0);
    return `${line} L ${lastPoint.x} ${chartH} L ${firstPoint.x} ${chartH} Z`;
  };

  return (
    <div className={`rounded-sm ${compact.padding} bg-white h-full flex flex-col`}>
      <CardHeader
        title="Financijski pregled"
        subtitle={`Pregled · ${rangeLabel(dateRange)}`}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help gap-1">
                <span className={`${compact.metric} font-bold ${profit >= 0 ? text.primary : negative.text}`}>
                  €{profit.toLocaleString("hr-HR")}
                </span>
                <DeltaBadge value={marginPercent} />
              </div>
            }
          >
            <TooltipTitle>Prihodi i rashodi (YTD)</TooltipTitle>
            <TooltipRow label="Prihodi" value={`€${totalRevenue.toLocaleString("hr-HR")}`} />
            <TooltipRow label="Rashodi" value={`€${totalExpenses.toLocaleString("hr-HR")}`} />
            <TooltipDivider />
            <TooltipRow
              label="Profit"
              value={`€${profit.toLocaleString("hr-HR")} (${marginPercent.toFixed(1)}%)`}
              emphasis={profit >= 0 ? "positive" : "negative"}
            />
          </DashboardTooltip>
        }
      />

      {data.totalRevenue === 0 && data.totalExpense === 0 ? (
        <NoResults title="Nema podataka o prihodima i rashodima" />
      ) : (
        <>
      <div className="mx-auto mb-2 relative w-full flex-1 flex flex-col min-h-[120px]">
        <div ref={chartRef} className="relative flex-1 min-h-[120px]">
        <svg
          viewBox={`0 0 ${chartW} ${chartH}`}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          className="overflow-visible w-full h-full animate-chartWipe"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <defs>
            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={positive.hex} stopOpacity="0.3" />
              <stop offset="100%" stopColor={positive.hex} stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={negative.hex} stopOpacity="0.25" />
              <stop offset="100%" stopColor={negative.hex} stopOpacity="0.02" />
            </linearGradient>
          </defs>

          <path d={buildAreaPath(revenue)} fill="url(#revGrad)" />
          <path d={buildSmoothPath(revenue)} fill="none" stroke={positive.hex} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />

          <path d={buildAreaPath(expenses)} fill="url(#expGrad)" />
          <path d={buildSmoothPath(expenses)} fill="none" stroke={negative.hex} strokeWidth="1.5" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />

          {months.map((_, i) => {
            const revPt = toPoint(revenue[i], i);
            const zoneW = chartW / months.length;
            const zoneX = i * zoneW;
            return (
              <g key={i} onMouseEnter={() => setHoverIndex(i)} onClick={() => handleBucketClick(i)} style={{ cursor: "pointer" }}>
                <rect x={zoneX} y={0} width={zoneW} height={chartH} fill="transparent" />
                {hoverIndex === i && (
                  <line x1={revPt.x} y1={0} x2={revPt.x} y2={chartH} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
                )}
              </g>
            );
          })}
        </svg>

        {/* Hover točke kao HTML krugovi (SVG bi ih zbog preserveAspectRatio="none" izobličio u elipse) */}
        {hoverIndex !== null && (() => {
          const revPt = toPoint(revenue[hoverIndex], hoverIndex);
          const expPt = toPoint(expenses[hoverIndex], hoverIndex);
          return (
            <>
              <span
                className="absolute w-2 h-2 rounded-full border-2 border-white -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ left: `${(revPt.x / chartW) * 100}%`, top: `${(revPt.y / chartH) * 100}%`, backgroundColor: positive.hex }}
              />
              <span
                className="absolute w-2 h-2 rounded-full border-2 border-white -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ left: `${(expPt.x / chartW) * 100}%`, top: `${(expPt.y / chartH) * 100}%`, backgroundColor: negative.hex }}
              />
            </>
          );
        })()}
        </div>

        {hoverIndex !== null && chartRef.current && createPortal(
          (() => {
            const r = chartRef.current!.getBoundingClientRect();
            const pt = toPoint(revenue[hoverIndex], hoverIndex);
            const x = r.left + (pt.x / chartW) * r.width;
            const y = r.top + (pt.y / chartH) * r.height;
            return (
              <div
                className="fixed z-[60] min-w-[180px] bg-slate-800 text-white text-sm rounded-lg shadow-xl p-3 pointer-events-none"
                style={{ left: x, top: y - 8, transform: "translate(-50%, -100%)" }}
              >
                <BarTooltipTitle>{months[hoverIndex].label}</BarTooltipTitle>
                <BarTooltipRow label="Prihodi" value={`€${revenue[hoverIndex].toLocaleString("hr-HR")}`} emphasis="positive" />
                <BarTooltipRow label="Rashodi" value={`€${expenses[hoverIndex].toLocaleString("hr-HR")}`} emphasis="negative" />
              </div>
            );
          })(),
          document.body
        )}

        <div className="flex justify-between mt-1">
          <AxisLabels labels={months.map((m) => m.label)} className="text-sm text-slate-400" />
        </div>
      </div>

      <div className="relative group flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3 cursor-help">
        <div className="flex items-center gap-1.5">
          <span className={`w-4 h-0.5 ${positive.solid} rounded`} />
          <span className={`text-sm ${text.muted}`}>Prihodi</span>
          <DeltaBadge value={revChange} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded" style={{ backgroundImage: `repeating-linear-gradient(90deg, ${negative.hex} 0, ${negative.hex} 4px, transparent 4px, transparent 7px)` }} />
          <span className={`text-sm ${text.muted}`}>Rashodi</span>
          <DeltaBadge value={expChange} inverted />
        </div>
        <LegendTooltip>
          <BarTooltipTitle>Promjena vs prethodni mjesec</BarTooltipTitle>
          <BarTooltipRow label="Prihodi" value={`€${prev >= 0 ? revenue[prev].toLocaleString("hr-HR") : 0} → €${revenue[last]?.toLocaleString("hr-HR") ?? 0}`} emphasis="positive" />
          <BarTooltipRow label="Rashodi" value={`€${prev >= 0 ? expenses[prev].toLocaleString("hr-HR") : 0} → €${expenses[last]?.toLocaleString("hr-HR") ?? 0}`} emphasis="negative" />
        </LegendTooltip>
      </div>
        </>
      )}
    </div>
  );
};

export default RevenueExpenseCard;
